/**
 * URL state for Platform ▸ Coverage: parsing the route's search params, deriving the API queries
 * from them, and sort toggling. Pure functions only, so the route's `validateSearch`, the page and
 * the tests share one definition.
 */

import type { CoverageReportTab } from '~/features/coverage-report/types/coverage-report.api.types';
import {
  COVERAGE_COLOR_TIERS,
  COVERAGE_GAP_REASONS,
} from '~/features/coverage-report/types/coverage-v3.types';

import type {
  PlatformCoverageFilters,
  PlatformCoverageRowsQuery,
  PlatformCoverageSearch,
  PlatformCoverageSortBy,
} from '../types/platform-coverage.types';

/** Rows per page (the API allows 1–100). */
export const PLATFORM_COVERAGE_PAGE_SIZE = 25;
/** The API answers 422 beyond this many `subsidiaryIds`. */
export const MAX_SUBSIDIARY_IDS = 100;
/** Debounce before a typed subsidiary search is written to the URL (and fetched). */
export const SEARCH_DEBOUNCE_MS = 300;
/** The coverage health ranges the Accounts filter offers, best first (`colorTier`; `NONE` = no data). */
export const COVERAGE_HEALTH_TIERS = [...COVERAGE_COLOR_TIERS, 'NONE'] as const;

/** Connectors coverage runs for — the connector filter's options before facets arrive. */
export const COVERAGE_CONNECTORS = ['GOOGLE_MERCHANT', 'META_COMMERCE'] as const;

export const DEFAULT_TAB: CoverageReportTab = 'production';
export const TABS: readonly CoverageReportTab[] = ['production', 'experiments'];
/** The server's order when `sortBy` is omitted: worst coverage first. */
export const DEFAULT_SORT: PlatformCoverageSortBy = 'productDetailsCoveragePct';
export const DEFAULT_SORT_DESC = false;
export const SORTS: readonly PlatformCoverageSortBy[] = [
  'subsidiaryName',
  'connectorCode',
  'snapshotDate',
  'productDetailsCoveragePct',
];

/**
 * First click on a column sorts the way a reader looks first: names A→Z, the oldest check and the
 * lowest live share first (what needs attention). A row's check day is its newest connection's, so
 * a partly stale row sorts among the fresh ones.
 */
const FIRST_DESC: Record<PlatformCoverageSortBy, boolean> = {
  subsidiaryName: false,
  connectorCode: false,
  snapshotDate: false,
  productDetailsCoveragePct: false,
};

const MAX_SEARCH_LENGTH = 200;
/** The API answers 422 to a `subsidiaryIds` value that is not a UUID. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/**
 * Accepts `'id'` or `['id', …]`; keeps UUIDs only (anything else would 422 every read), drops
 * duplicates and caps at the API's limit.
 */
function uuidList(value: unknown): string[] | undefined {
  const raw = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  const items = [
    ...new Set(
      raw
        .filter((v): v is string => typeof v === 'string')
        .map((v) => v.trim().toLowerCase())
        .filter((v) => UUID.test(v)),
    ),
  ].slice(0, MAX_SUBSIDIARY_IDS);
  return items.length > 0 ? items : undefined;
}

function positiveInt(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : undefined;
}

function bool(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

/**
 * The route's `validateSearch`. Every key is optional and kept only when valid; defaults are
 * dropped so the URL stays short. An unknown reason or connector, or a subsidiary id that is not a
 * UUID, never reaches the API (a 422).
 */
export function parsePlatformCoverageSearch(
  search: Record<string, unknown>,
): PlatformCoverageSearch {
  const out: PlatformCoverageSearch = {};

  const tab = oneOf(TABS, search.tab);
  if (tab && tab !== DEFAULT_TAB) out.tab = tab;

  const subsidiaryId = uuidList(search.subsidiaryId);
  if (subsidiaryId) out.subsidiaryId = subsidiaryId;

  const connector = oneOf(COVERAGE_CONNECTORS, search.connector);
  if (connector) out.connector = connector;

  if (typeof search.search === 'string' && search.search.trim() !== '') {
    out.search = search.search.slice(0, MAX_SEARCH_LENGTH);
  }

  const reason = oneOf(COVERAGE_GAP_REASONS, search.reason);
  if (reason) out.reason = reason;

  const tier = oneOf(COVERAGE_HEALTH_TIERS, search.tier);
  if (tier) out.tier = tier;

  const sortBy = oneOf(SORTS, search.sortBy);
  if (sortBy) {
    const sortDesc = bool(search.sortDesc) ?? FIRST_DESC[sortBy];
    if (sortBy !== DEFAULT_SORT || sortDesc !== DEFAULT_SORT_DESC) {
      out.sortBy = sortBy;
      out.sortDesc = sortDesc;
    }
  }

  const page = positiveInt(search.page);
  if (page && page > 1) out.page = page;

  return out;
}

/** Filters shared by rows, stats and insights. */
export function filtersOf(search: PlatformCoverageSearch): PlatformCoverageFilters {
  return {
    tab: search.tab ?? DEFAULT_TAB,
    subsidiaryIds: search.subsidiaryId ?? [],
    connectorCode: search.connector,
    search: search.search,
    colorTier: search.tier,
  };
}

/** The rows query: filters plus reason, sort and page. */
export function rowsQueryOf(search: PlatformCoverageSearch): PlatformCoverageRowsQuery {
  return {
    ...filtersOf(search),
    reason: search.reason,
    sortBy: search.sortBy,
    sortDesc: search.sortDesc,
    page: search.page ?? 1,
    size: PLATFORM_COVERAGE_PAGE_SIZE,
  };
}

/** The sort in effect, the server default spelled out. */
export function sortOf(search: PlatformCoverageSearch): {
  sortBy: PlatformCoverageSortBy;
  sortDesc: boolean;
} {
  return {
    sortBy: search.sortBy ?? DEFAULT_SORT,
    sortDesc: search.sortDesc ?? DEFAULT_SORT_DESC,
  };
}

/**
 * Sort after a header click: the active column flips, a new one starts in its first direction.
 * Returns the search with the default order omitted and paging reset.
 */
export function withSortToggled(
  search: PlatformCoverageSearch,
  column: PlatformCoverageSortBy,
): PlatformCoverageSearch {
  const current = sortOf(search);
  const sortDesc = current.sortBy === column ? !current.sortDesc : FIRST_DESC[column];
  const isDefault = column === DEFAULT_SORT && sortDesc === DEFAULT_SORT_DESC;
  const rest = omitKeys(search, ['sortBy', 'sortDesc', 'page']);
  return isDefault ? rest : { ...rest, sortBy: column, sortDesc };
}

/** Copies `search` without the given keys (no dynamic `delete`). */
function omitKeys(search: PlatformCoverageSearch, keys: readonly string[]): PlatformCoverageSearch {
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !keys.includes(key)),
  ) as PlatformCoverageSearch;
}

/** Keys "Clear filters" resets — everything except tab and sort. */
const FILTER_KEYS = [
  'subsidiaryId',
  'connector',
  'search',
  'reason',
  'tier',
] as const satisfies readonly (keyof PlatformCoverageSearch)[];

/** True when any narrowing filter is set — switches the empty copy to "no matches". */
export function hasActiveFilters(search: PlatformCoverageSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

/**
 * Applies a filter patch: drops keys set to `undefined` / empty arrays and resets the page, since a
 * new filter means page one of a different result set.
 */
export function withFilterPatch(
  search: PlatformCoverageSearch,
  patch: Partial<PlatformCoverageSearch>,
): PlatformCoverageSearch {
  const merged = Object.entries({ ...search, ...patch }).filter(
    ([key, value]) =>
      value !== undefined && !(Array.isArray(value) && value.length === 0) && key !== 'page',
  );
  return Object.fromEntries(merged) as PlatformCoverageSearch;
}

/** Keys whose change is a step Back should undo; typing, sorting and paging are not. */
const HISTORY_KEYS = [
  'tab',
  'subsidiaryId',
  'connector',
  'reason',
  'tier',
] as const satisfies readonly (keyof PlatformCoverageSearch)[];

/**
 * Whether moving from `prev` to `next` replaces the history entry: true unless the tab or a
 * subsidiary, connector or issue filter changed (those push, so Back restores the previous set).
 */
export function replacesHistory(
  prev: PlatformCoverageSearch,
  next: PlatformCoverageSearch,
): boolean {
  return HISTORY_KEYS.every(
    (key) => JSON.stringify(prev[key] ?? null) === JSON.stringify(next[key] ?? null),
  );
}

/** Clears the filters and the page; keeps tab and sort. */
export function clearFilters(search: PlatformCoverageSearch): PlatformCoverageSearch {
  return omitKeys(search, [...FILTER_KEYS, 'page']);
}

/**
 * The page to move to when `page` (1-based) is past the end of a non-empty result — a shared link
 * to page 9, or rows that disappeared since. `undefined` (= page one) for the first page; `null`
 * when the page is fine.
 */
export function clampPage(
  page: number,
  result: { rows: number; totalPages: number },
): number | undefined | null {
  if (result.rows > 0 || result.totalPages === 0 || page <= result.totalPages) return null;
  return result.totalPages > 1 ? result.totalPages : undefined;
}
